import { type RefObject, useEffect } from 'react';

type DeferredVideoSource = {
  src: string;
  type: string;
};

type DeferredVideoOptions = {
  activationRef?: RefObject<Element | null>;
  deferUntilInteraction?: boolean;
  onActivate?: (video: HTMLVideoElement) => void;
  pauseWhenOutOfView?: boolean;
  posterSrc?: string;
  rootMargin?: string;
  src: string | readonly DeferredVideoSource[];
  videoRef: RefObject<HTMLVideoElement | null>;
};

const DEFAULT_ROOT_MARGIN = '0px';

function reportPlaybackError(error: unknown) {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return;
  }

  console.error('Deferred video playback failed', error);
}

function playVideo(video: HTMLVideoElement) {
  void video.play().catch(reportPlaybackError);
}

function setVideoSource(
  video: HTMLVideoElement,
  src: DeferredVideoOptions['src'],
) {
  video
    .querySelectorAll('source[data-deferred-video-source]')
    .forEach((source) => source.remove());

  if (typeof src === 'string') {
    video.src = src;
    return;
  }

  video.removeAttribute('src');
  src.forEach((source) => {
    const sourceElement = document.createElement('source');
    sourceElement.dataset.deferredVideoSource = '';
    sourceElement.src = source.src;
    sourceElement.type = source.type;
    video.append(sourceElement);
  });
}

export function useDeferredVideo({
  activationRef,
  deferUntilInteraction = false,
  onActivate = playVideo,
  pauseWhenOutOfView = false,
  posterSrc,
  rootMargin = DEFAULT_ROOT_MARGIN,
  src,
  videoRef,
}: DeferredVideoOptions) {
  useEffect(() => {
    const video = videoRef.current;
    const activationTarget = activationRef?.current ?? video;
    if (!video || !activationTarget) {
      return undefined;
    }

    if (posterSrc) {
      video.poster = posterSrc;
    }

    let activated = false;
    let isIntersecting = false;
    let isVisible = !pauseWhenOutOfView;
    let hasUserIntent = !deferUntilInteraction;

    const activate = () => {
      if (activated || !isIntersecting || !hasUserIntent) {
        return;
      }
      activated = true;

      setVideoSource(video, src);
      video.preload = 'auto';
      video.load();
      if (isVisible) {
        onActivate(video);
      }
    };

    const removeUserIntentListeners = () => {
      activationTarget.removeEventListener('click', markUserIntent);
    };
    function markUserIntent() {
      hasUserIntent = true;
      removeUserIntentListeners();
      activate();
    }
    if (deferUntilInteraction && !hasUserIntent) {
      activationTarget.addEventListener('click', markUserIntent, {
        once: true,
      });
    }

    let observer: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            return;
          }

          isIntersecting = true;
          observer?.disconnect();
          activate();
        },
        { rootMargin },
      );

      observer.observe(activationTarget);
    } else {
      isIntersecting = true;
      isVisible = true;
      activate();
    }

    let visibilityObserver: IntersectionObserver | undefined;
    if (pauseWhenOutOfView && 'IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting;
          if (!activated) {
            return;
          }

          if (isVisible) {
            onActivate(video);
          } else {
            video.pause();
          }
        },
        { threshold: 0.01 },
      );
      visibilityObserver.observe(activationTarget);
    }

    return () => {
      observer?.disconnect();
      visibilityObserver?.disconnect();
      removeUserIntentListeners();
      video.pause();
    };
  }, [
    activationRef,
    deferUntilInteraction,
    onActivate,
    pauseWhenOutOfView,
    posterSrc,
    rootMargin,
    src,
    videoRef,
  ]);
}
